import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const STEPS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
const STEP_LABELS = ['Order Placed', 'Confirmed', 'Preparing', 'Shipped', 'Delivered'];
const STEP_ICONS = ['🛒', '✅', '📦', '🚚', '🎉'];

const STATUS_COLORS = {
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  confirmed: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  processing: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  shipped: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  delivered: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  cancelled: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

// Reusable stepper component
function OrderStepper({ status }) {
  if (status === 'cancelled') {
    return (
      <div className="flex items-center justify-center py-4">
        <span className="bg-red-50 text-red-600 border border-red-200 text-xs font-sans tracking-widest uppercase px-4 py-2">
          ❌ Order Cancelled
        </span>
      </div>
    );
  }

  const currentIdx = STEPS.indexOf(status);

  return (
    <div className="py-4">
      {/* Desktop stepper */}
      <div className="hidden md:flex items-center justify-between">
        {STEPS.map((step, i) => {
          const isCompleted = i <= currentIdx;
          const isCurrent = i === currentIdx;

          return (
            <React.Fragment key={step}>
              {/* Step circle + label */}
              <div className="flex flex-col items-center relative" style={{ flex: '0 0 auto' }}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans font-bold transition-all duration-500 ${
                  isCompleted
                    ? 'bg-lux-gold text-white shadow-sm'
                    : 'bg-gray-100 text-gray-400'
                } ${isCurrent ? 'ring-2 ring-lux-gold/30 ring-offset-2' : ''}`}>
                  {isCompleted ? '✓' : (i + 1)}
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-[10px] font-sans tracking-widest uppercase ${
                    isCompleted ? 'text-lux-gold font-semibold' : 'text-gray-400'
                  }`}>{STEP_LABELS[i]}</p>
                </div>
              </div>

              {/* Connector line */}
              {i < STEPS.length - 1 && (
                <div className="flex-1 mx-2 h-0.5 relative" style={{ marginTop: '-12px' }}>
                  <div className="absolute inset-0 bg-gray-100 rounded-full" />
                  <div
                    className="absolute inset-y-0 left-0 bg-lux-gold rounded-full transition-all duration-700"
                    style={{ width: i < currentIdx ? '100%' : '0%' }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile stepper (vertical) */}
      <div className="md:hidden space-y-0">
        {STEPS.map((step, i) => {
          const isCompleted = i <= currentIdx;
          const isCurrent = i === currentIdx;

          return (
            <div key={step} className="flex items-start gap-3">
              {/* Circle + vertical line */}
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-sans font-bold flex-shrink-0 ${
                  isCompleted ? 'bg-lux-gold text-white' : 'bg-gray-100 text-gray-400'
                } ${isCurrent ? 'ring-2 ring-lux-gold/30 ring-offset-1' : ''}`}>
                  {isCompleted ? '✓' : (i + 1)}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`w-0.5 h-6 ${i < currentIdx ? 'bg-lux-gold' : 'bg-gray-100'}`} />
                )}
              </div>
              {/* Label */}
              <div className="pt-1">
                <p className={`text-xs font-sans ${isCompleted ? 'text-lux-primary font-medium' : 'text-gray-400'}`}>
                  {STEP_LABELS[i]}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

import { formatImageUrl } from '../utils/imageUtils';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    api.get('/orders/my-orders').then(res => setOrders(res.data.orders)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-20 py-10 animate-fade-in">
      <p className="label-sm text-lux-gold mb-2">Your History</p>
      <h1 className="font-display text-4xl text-lux-primary mb-10">My Orders</h1>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-40 w-full" />)}</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20 bg-white border border-lux-border">
          <p className="text-4xl mb-4">🛍️</p>
          <p className="font-display text-3xl text-lux-primary mb-3">No orders yet</p>
          <p className="text-sm font-sans text-lux-secondary mb-6">Your luxury journey awaits</p>
          <Link to="/shop" className="btn-primary inline-block">Start Shopping</Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map(order => {
            const isExpanded = expandedOrder === order._id;
            const sc = STATUS_COLORS[order.status] || STATUS_COLORS.pending;

            return (
              <div key={order._id} className="bg-white border border-lux-border overflow-hidden transition-shadow hover:shadow-sm">
                {/* Order Header — always visible */}
                <button
                  onClick={() => setExpandedOrder(isExpanded ? null : order._id)}
                  className="w-full text-left p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <p className="font-sans text-sm font-semibold text-lux-primary">{order.orderNumber}</p>
                      <span className={`text-[10px] font-sans tracking-widest uppercase px-2.5 py-0.5 border ${sc.bg} ${sc.text} ${sc.border}`}>
                        {order.status}
                      </span>
                      <span className="text-[10px] font-sans text-lux-secondary">
                        {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Paid Online'}
                      </span>
                    </div>
                    <p className="font-sans text-xs text-lux-secondary">
                      {new Date(order.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                      {' · '}{order.products.length} item{order.products.length !== 1 ? 's' : ''}
                    </p>
                    {order.estimatedDelivery && order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <p className="font-sans text-xs text-lux-gold mt-1">
                        Estimated delivery: {new Date(order.estimatedDelivery).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-display text-2xl text-lux-primary">₹{order.totalAmount.toLocaleString('en-IN')}</p>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                      className={`text-lux-secondary transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-lux-border animate-fade-in">
                    {/* Visual Stepper */}
                    <div className="px-6 pt-4 pb-2">
                      <OrderStepper status={order.status} />
                    </div>

                    {/* Status Timeline */}
                    {order.statusHistory && order.statusHistory.length > 0 && (
                      <div className="px-6 pb-4">
                        <p className="label-sm text-lux-secondary mb-3">Order Timeline</p>
                        <div className="space-y-0">
                          {[...order.statusHistory].reverse().map((entry, idx) => (
                            <div key={idx} className="flex items-start gap-3 py-2">
                              <div className="flex flex-col items-center">
                                <div className={`w-2 h-2 rounded-full mt-1.5 ${idx === 0 ? 'bg-lux-gold' : 'bg-gray-200'}`} />
                                {idx < order.statusHistory.length - 1 && <div className="w-px h-4 bg-gray-200" />}
                              </div>
                              <div className="flex-1">
                                <p className={`text-xs font-sans capitalize ${idx === 0 ? 'text-lux-primary font-medium' : 'text-lux-secondary'}`}>
                                  {STEP_ICONS[STEPS.indexOf(entry.status)] || '•'} {entry.status}
                                </p>
                                <p className="text-[10px] font-sans text-gray-400">
                                  {new Date(entry.timestamp).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} at {new Date(entry.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Divider */}
                    <div className="h-px bg-lux-border mx-6" />

                    {/* Items List */}
                    <div className="p-6">
                      <p className="label-sm text-lux-secondary mb-4">Items</p>
                      <div className="space-y-3">
                        {order.products.map((item, idx) => (
                          <div key={idx} className="flex gap-4 items-center">
                            <img
                              src={formatImageUrl(item.image) || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80'}
                              alt={item.name}
                              className="w-14 h-18 object-cover bg-lux-bg border border-lux-border flex-shrink-0"
                              onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80'; }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-sans font-medium text-lux-primary truncate">{item.name}</p>
                              <p className="text-[10px] text-lux-secondary uppercase tracking-widest">
                                {item.brand}
                                {item.size && ` · Size: ${item.size}`}
                                {item.color && ` · ${item.color}`}
                                {' · Qty: '}{item.quantity}
                              </p>
                            </div>
                            <p className="font-sans text-sm font-semibold text-lux-primary flex-shrink-0">
                              ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Order Total Breakdown */}
                      <div className="mt-6 pt-4 border-t border-lux-border space-y-2">
                        <div className="flex justify-between">
                          <span className="text-xs font-sans text-lux-secondary">Subtotal</span>
                          <span className="text-xs font-sans text-lux-primary">₹{order.totalAmount.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs font-sans text-lux-secondary">Shipping</span>
                          <span className="text-xs font-sans text-green-600">{order.shippingFee > 0 ? `₹${order.shippingFee}` : 'Free'}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-lux-border">
                          <span className="text-sm font-sans font-semibold text-lux-primary">Total</span>
                          <span className="text-sm font-sans font-semibold text-lux-primary">₹{order.totalAmount.toLocaleString('en-IN')}</span>
                        </div>
                      </div>

                      {/* Delivery Address */}
                      {order.shippingAddress && (
                        <div className="mt-6 pt-4 border-t border-lux-border">
                          <p className="label-sm text-lux-secondary mb-2">Delivery Address</p>
                          <p className="text-sm font-sans text-lux-primary">
                            {order.shippingAddress.fullName}<br />
                            <span className="text-lux-secondary">
                              {order.shippingAddress.address}
                              {order.shippingAddress.city && `, ${order.shippingAddress.city}`}
                              {order.shippingAddress.state && `, ${order.shippingAddress.state}`}<br />
                              {order.shippingAddress.zipCode}{order.shippingAddress.country && `, ${order.shippingAddress.country}`}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
